/* eslint-disable @typescript-eslint/no-non-null-assertion */

type TIntlTelInput = typeof import('intl-tel-input');

type TIntlTelInputInstance = ReturnType<TIntlTelInput>;
type TValidationError = typeof window.intlTelInputUtils.validationError;
type TErrorsDict = TValidationError & Record<string, number>;

/**
 * True types with properties that were hidden initially
 */
type TTrueIntlTelInput = import('intl-tel-input').Plugin & { id: number };
type TTrueItlTelInputGlobals = import('intl-tel-input').Static & { instances: Record<number, unknown> };

/**
 * @description Base InputTel implementation
 * @param InputTel Base widget for extending
 * @returns CountryCodeInput class
 */
function CountryCodeInputClassCreator(InputTel: ReturnType<typeof import('./InputTel').default>) {
    /**
     * @category widgets
     * @subcategory forms
     * @class CountryCodeInput
     * @classdesc This is extension on input tel to add ability to select and change country code to telephone number.
     */
    class CountryCodeInput extends InputTel {
        intlTelInstance?: TIntlTelInputInstance;

        intlTelProto?: TIntlTelInput;

        prefs() {
            return {
                defaultCountry: 'us',
                ...super.prefs()
            };
        }

        init() {
            return this.loadPlugin().then(([intlTelInput]) => {
                this.intlTelProto = intlTelInput.default;

                this.initPlugin();
            });
        }

        /**
         * @description Create instance of the plugin
         */
        initPlugin() {
            const phoneInputField = this.ref('field').get<HTMLInputElement>();

            if (!phoneInputField) {
                return;
            }

            this.intlTelInstance = this.intlTelProto!(phoneInputField, {
                initialCountry: this.prefs().defaultCountry,
                onlyCountries: this.getAllowedCountries(),
                excludeCountries: this.getExcludedCountries(),
                preferredCountries: ['us', 'ca', 'gb', 'de', 'ae'],
                nationalMode: true
            });
        }

        /**
         * @description Load the plugin library
         * @returns Module promise
         */
        loadPlugin() {
            return <unknown> Promise.all([
                import(/* webpackChunkName: 'intlTelInputPlugin' */'intl-tel-input'),
                import(/* webpackChunkName: 'intlTelInputPlugin' */'intl-tel-input/build/js/utils.js')
                // this lib is required for validation and placeholders. It inserted into window.intlTelInputUtils
                // after loading
            ]) as Promise<[TTypedAsyncImport<TIntlTelInput>]>;
        }

        /**
         * @description Destroys plugin instance
         */
        destroyPluginInstance() {
            if (this.intlTelInstance) {
                try {
                    this.intlTelInstance.destroy();
                } catch (_) {
                    /**
                     * Original destroy method could not work properly and fail,
                     * so we making sure the instance is deleted.
                     */
                    const instances = (<TTrueItlTelInputGlobals> window.intlTelInputGlobals).instances;

                    delete instances[(<TTrueIntlTelInput> this.intlTelInstance).id];
                } finally {
                    this.intlTelInstance = undefined;
                }
            }
        }

        /**
         * @description Resets plugin instance
         */
        reinitPlugin() {
            if (!this.intlTelInstance) {
                this.initPlugin();

                return;
            }

            this.destroyPluginInstance();

            this.initPlugin();

            const phoneInputField = this.ref('field').get<HTMLInputElement>();

            if (phoneInputField && phoneInputField.value) {
                /**
                 * Setting country via constructor will not work if the input field has some data,
                 * so we're doing it manually here.
                 */
                this.intlTelInstance!.setCountry(this.prefs().defaultCountry);
            }
        }

        /**
         * @description Callback on widget refresh
         */
        onRefresh() {
            setTimeout(() => this.reinitPlugin());
        }

        /**
         * @description Set input value
         * @param [newVal] - set this value to input
         * @param [silently] - if set to `true` - input should not be validated
         * against a new value and no events should be fired
         */
        setValue(newVal = '', silently = false) {
            super.setValue(newVal, silently);
            this.ref('field').attr('value', newVal);
            this.intlTelInstance?.setNumber(newVal);
        }

        /**
         * @description Gets formatted phone number
         */
        getValue() {
            return this.intlTelInstance?.getNumber().toString() || '';
        }

        /**
         * @description Check if input is valid
         * @returns return validation check status
         */
        isValid(): boolean {
            if (!super.isValid()) {
                return false;
            } else if (!this.getValue()) {
                return true;
            }

            const isPhoneValid = this.intlTelInstance!.isValidNumber() && this.containsAllowedSymbols();

            if (!isPhoneValid) {
                this.error = this.getErrorMessage(this.intlTelInstance!.getValidationError());
            }

            return isPhoneValid;
        }

        /**
         * @description Tests initially inputted phone number value for not allowed symbols
         * @returns returns true if contains allowed symbols only
         */
        containsAllowedSymbols(): boolean {
            const regexStr = this.prefs().validationConfig.patterns.parse;
            const allowedCharactersRegex = new RegExp(regexStr);
            const rawInput = super.getValue();

            return allowedCharactersRegex.test(rawInput);
        }

        /**
         * @description Get error message associated with error code
         * @param errorCode code of the error
         * @returns return error message
         */
        getErrorMessage(errorCode: number): string {
            let errorMessage = '';
            const errorsDict = <TErrorsDict> window.intlTelInputUtils.validationError;

            switch (errorCode) {
                case errorsDict.IS_POSSIBLE:
                case errorsDict.IS_POSSIBLE_LOCAL_ONLY:
                case errorsDict.INVALID_COUNTRY_CODE:
                case errorsDict.INVALID_LENGTH:
                    errorMessage = this.prefs().validationConfig.errors.parse;
                    break;
                case errorsDict.TOO_SHORT:
                    errorMessage = this.prefs().validationConfig.errors.minLength;
                    break;
                case errorsDict.TOO_LONG:
                    errorMessage = this.prefs().validationConfig.errors.maxLength;
                    break;
                default:
                    errorMessage = '';
            }

            return errorMessage;
        }

        /**
         * @description Get array of countries which must only be presented in country code drop-down list
         * Returns array of country codes
         */
        getAllowedCountries(): Array<string> {
            const allowedCountries = this.ref('field').attr('data-allowed-countries');

            return this.parseCountriesConfig(allowedCountries);
        }

        /**
         * @description Get array of countries which must not be presented in country code drop-down list
         * Returns array of country codes
         */
        getExcludedCountries(): Array<string> {
            const excludedCountries = this.ref('field').attr('data-excluded-countries');

            return this.parseCountriesConfig(excludedCountries);
        }

        /**
         * @description Validates the string with country codes to be an uppercase list of country codes separated by semicolon, e.g.: 'CA;US;GB'
         * Also separates the string to an array of country codes
         * @param countriesList Not validated string of country codes
         * Returns array of country codes
         */
        parseCountriesConfig(countriesList: string): Array<string> {
            const isCorrect = /^([A-Z]{2};)*[A-Z]{2}$/.test(countriesList);

            if (!isCorrect || !countriesList.length) {
                return [];
            }

            return countriesList.split(';');
        }
    }

    return CountryCodeInput;
}

export type TCountryCodeInput = ReturnType<typeof CountryCodeInputClassCreator>;

export type TCountryCodeInputInstance = InstanceType<TCountryCodeInput>;

export default CountryCodeInputClassCreator;
